"use client"

import { useCallback } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"

export interface CurrentTenant {
  id: string
  name: string
  logo_url?: string | null
  is_active: boolean
  created_at: string
  /** Owner of the tenant (the original creator / billing-responsible user).
   *  Used as the owner-gate for the Organization page (Danger zone, edits). */
  owner_user_id?: string | null
  /** Organization-wide default timezone (IANA). New users inherit it; the
   *  user profile gains a "Use organization timezone" copy button. */
  timezone?: string | null
  /** Whether the organization observes DST in the configured timezone. */
  uses_dst?: boolean | null
  working_hours_start?: string | null
  working_hours_end?: string | null
  /** Set when the organization is archived; null/absent for a live tenant. */
  archived_at?: string | null
  /** When the archived tenant is scheduled for permanent purge (archive + 30d). */
  archive_scheduled_purge_at?: string | null
  /** Billing channel — drives the Shopify onboarding gate (`shopify` | `stripe`). */
  billing_channel?: string | null
}

export const currentTenantQueryPrefix = ["tenant", "current"] as const

export const currentTenantQueryKey = (tenantId: string | null) =>
  [...currentTenantQueryPrefix, tenantId] as const

/** The one client-side entry for the active tenant. */
export function useCurrentTenantQuery() {
  const { fetchWithAuth, isAuthenticated, isLoading: authLoading } = useAuth()
  const { activeTenantId } = usePermissions()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: currentTenantQueryKey(activeTenantId),
    enabled: isAuthenticated && Boolean(activeTenantId),
    // Avoid unnecessary tenant refetches while the archive notice is displayed.
    // Session polling independently detects revocation.
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<CurrentTenant> => {
      const response = await fetchWithAuth("/api/tenants/current")
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload.error || "Failed to load tenant")
      }
      const { tenant } = await response.json() as { tenant: CurrentTenant }
      return tenant
    },
  })

  // By prefix, not the tenant-scoped key: a mount-time capture freezes the null tenant id
  // of the auth-bootstrap frame, and key matching is element-wise.
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: currentTenantQueryPrefix }),
    [queryClient],
  )

  return {
    tenant: query.data ?? null,
    // The query reports `isLoading: false` while auth bootstraps. Only the first fetch
    // counts: a query that never succeeded goes back to pending on every refetch, and the
    // app gate would unmount the consumers whose remount starts the next one.
    isLoading: authLoading || (query.isLoading && !query.isFetched),
    error: query.error,
    refresh,
  }
}
